import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Newspaper, Rss } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { NewsCard, SkeletonCard } from "@/components/news/NewsCard";
import { API } from "@/lib/newsUtils";
import { useSeo } from "@/lib/seo";
import { trackDwell } from "@/lib/engagement";

export default function MediaPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const startRef = useRef(Date.now());
  const sourceRef = useRef(null);

  useEffect(() => { if (data?.name) sourceRef.current = data.name; }, [data]);

  // Track time spent on this media (dwell) for relevance ranking.
  useEffect(() => {
    startRef.current = Date.now();
    const flush = () => {
      if (sourceRef.current) {
        trackDwell(sourceRef.current, Date.now() - startRef.current);
        startRef.current = Date.now();
      }
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); flush(); };
  }, [slug]);

  useSeo({
    title: data ? `${data.name} — Noticias resumidas con IA | News Digest` : "Medio | News Digest",
    description: data
      ? `Todas las noticias de ${data.name} resumidas con IA. ${data.total} noticias analizadas en News Digest.`
      : undefined,
  });

  useEffect(() => {
    setStatus("loading");
    window.scrollTo(0, 0);
    (async () => {
      try {
        const res = await axios.get(`${API}/medios/${slug}`, { params: { page, page_size: 9 } });
        setData(res.data);
        setStatus("done");
      } catch {
        setStatus("error");
      }
    })();
  }, [slug, page]);

  const goToPage = (p) => setSearchParams(p > 1 ? { page: String(p) } : {});

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="media-page">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-lg font-black tracking-tight hidden sm:inline">NEWS DIGEST</span>
          </Link>
          <Button
            data-testid="media-back-button"
            variant="outline"
            onClick={() => navigate("/")}
            className="rounded-none border-border h-10 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Ir al feed
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-10">
        {status === "loading" && (
          <div>
            <div className="h-10 w-64 bg-muted animate-pulse mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="py-20 text-center" data-testid="media-not-found">
            <p className="font-heading text-2xl font-black tracking-tight mb-2">Medio no encontrado</p>
            <p className="text-muted-foreground mb-6">Todavía no tenemos noticias agrupadas de este medio.</p>
            <Button onClick={() => navigate("/")} className="rounded-none bg-primary hover:bg-blue-900 h-11 px-6">
              Volver al feed
            </Button>
          </div>
        )}

        {status === "done" && data && (
          <>
            <div className="mb-8 border-b border-border pb-6" data-testid="media-header">
              <div className="flex items-center gap-2 text-primary mb-2">
                <Rss className="w-4 h-4" />
                <span className="text-xs uppercase tracking-widest font-semibold">Medio</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight" data-testid="media-title">
                {data.name}
              </h1>
              <p className="text-muted-foreground mt-3">
                {data.total} noticia{data.total !== 1 ? "s" : ""} analizada{data.total !== 1 ? "s" : ""} con IA
              </p>
            </div>

            {data.articles.length === 0 ? (
              <p className="text-muted-foreground py-16 text-center" data-testid="media-empty">
                {page > 1 ? "No hay más noticias en esta página." : "Aún no hay noticias de este medio. Vuelve pronto."}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="media-grid">
                {data.articles.map((article, index) => (
                  <NewsCard key={article.id} article={article} index={index} hideMediaLink />
                ))}
              </div>
            )}

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-border" data-testid="media-pagination">
                <Button
                  data-testid="media-prev-page"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                  className="rounded-none border-border h-11 gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Anterior
                </Button>
                <span className="text-sm font-semibold text-muted-foreground" data-testid="media-page-indicator">
                  Página {page} de {data.total_pages}
                </span>
                <Button
                  data-testid="media-next-page"
                  disabled={!data.has_more}
                  onClick={() => goToPage(page + 1)}
                  className="rounded-none bg-primary hover:bg-blue-900 h-11 gap-2"
                >
                  Siguiente <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
